import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, ResponsiveTable, TableSkeleton, EmptyState, StatusBadge, AutoBadge, DateCell } from "@/admin/ui";
import { Upload, Play, Download, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const ACTIONS = ["grant_credits", "deduct_credits", "freeze", "unfreeze", "suspend", "unsuspend", "add_note"];

export default function AdminBulkOps() {
  const { t } = useLang();
  const [action, setAction] = React.useState("grant_credits");
  const [csvText, setCsvText] = React.useState("");
  const [dry, setDry] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [batches, setBatches] = React.useState(null);
  const [reason, setReason] = React.useState("");

  const loadBatches = React.useCallback(async () => {
    const r = await api.get("/api/admin/bulk/batches", { params: { limit: 20 } });
    setBatches(r.data.items);
  }, []);
  React.useEffect(() => { loadBatches(); }, [loadBatches]);

  const doDryRun = async () => {
    setBusy(true); setDry(null);
    try { const r = await api.post("/api/admin/bulk/dry-run", { action, csv_text: csvText }); setDry(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };
  const doExecute = async () => {
    if (!reason || reason.length < 3) return toast.error(t("adminp.bulk.reason_required"));
    setBusy(true);
    try {
      const r = await api.post("/api/admin/bulk/execute", { action, csv_text: csvText, reason, idempotency_key: dry?.idempotency_key });
      toast.success(`${r.data.processed_rows} processed · ${r.data.failed_rows} failed · ${r.data.skipped_rows} skipped${r.data.idempotent_replay ? " (replay)" : ""}`);
      setDry(null); setCsvText(""); setReason(""); loadBatches();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };
  const downloadTemplate = async () => {
    const r = await api.get("/api/admin/bulk/template", { params: { action }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `template-${action}.csv`; a.click();
  };

  return (
    <div className="space-y-6" data-testid="admin-bulk-ops">
      <PageHeader title={t("adminp.bulk.title")} description={t("adminp.bulk.subtitle")} />

      <SectionCard title="Prepare batch">
        <div className="flex gap-2 items-center flex-wrap">
          <select className="lp-input text-xs py-1.5" value={action} onChange={(e) => { setAction(e.target.value); setDry(null); }} data-testid="bulk-action">
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={downloadTemplate} data-testid="bulk-template">
            <Download className="w-3.5 h-3.5" /> {t("adminp.bulk.template")}
          </button>
        </div>
        <textarea className="lp-input w-full h-40 font-mono text-xs" placeholder="Paste CSV here..." value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="bulk-csv" />
        <div>
          <button className="lp-btn-secondary text-xs" onClick={doDryRun} disabled={busy || csvText.length < 10} data-testid="bulk-dryrun">
            <Play className="w-3.5 h-3.5" /> {t("adminp.bulk.dry_run")}
          </button>
        </div>
      </SectionCard>

      {dry && (
        <SectionCard title="Dry-run review" testid="bulk-dryrun-result">
          <div className="flex gap-3 flex-wrap text-xs">
            <StatusBadge tone="success" icon={CheckCircle2}>{dry.valid_rows} valid</StatusBadge>
            <StatusBadge tone="danger" icon={XCircle}>{dry.invalid_rows} invalid</StatusBadge>
            {dry.duplicate_row_indices.length > 0 && <StatusBadge tone="warning" icon={AlertTriangle}>{dry.duplicate_row_indices.length} dup rows</StatusBadge>}
          </div>
          {dry.header_errors.length > 0 && <div className="text-red-300 text-xs">Header errors: {dry.header_errors.join(", ")}</div>}
          {dry.existing_batch && <div className="text-amber-200 text-xs">Batch previously submitted: {dry.existing_batch.id.slice(0, 8)} · {dry.existing_batch.status}</div>}
          <div className="max-h-60 overflow-auto">
            <table className="w-full text-[10px]">
              <thead className="lp-table-sticky"><tr className="text-white/40"><th className="text-left">#</th><th className="text-left">Email</th><th className="text-left">Errors</th></tr></thead>
              <tbody>
                {dry.rows.slice(0, 20).map((r) => (
                  <tr key={r.row_index} className={r.valid ? "" : "text-red-300"}>
                    <td>{r.row_index}</td>
                    <td className="font-mono">{r.email}</td>
                    <td>{r.errors.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dry.valid_rows > 0 && !dry.header_errors.length && (
            <div className="space-y-2 pt-3 border-t border-white/10">
              <div className="text-xs text-white/60">
                {dry.invalid_rows > 0 && <span className="text-amber-200">Note: {dry.invalid_rows} invalid row(s) will be skipped. Only valid rows will be applied.</span>}
              </div>
              <input className="lp-input w-full text-xs" placeholder="Batch reason (min 3 chars, audit-logged)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bulk-reason" />
              <button className="lp-btn-primary text-xs" onClick={doExecute} disabled={busy || reason.length < 3} data-testid="bulk-execute">
                <Upload className="w-3.5 h-3.5" /> {t("adminp.bulk.execute")}
              </button>
            </div>
          )}
        </SectionCard>
      )}

      <SectionCard title={t("adminp.bulk.batches")}
        right={<button className="lp-btn-ghost" onClick={loadBatches} aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>}>
        {!batches ? <TableSkeleton rows={4} cols={8} /> : batches.length === 0 ? (
          <EmptyState title="No batches yet" />
        ) : (
          <ResponsiveTable minWidth={900}>
            <thead className="lp-table-sticky"><tr>
              <th>When</th><th>Action</th><th>Status</th>
              <th className="text-right">Ok</th><th className="text-right">Fail</th><th className="text-right">Skip</th>
              <th>Actor</th><th className="text-right"></th>
            </tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} data-testid={`bulk-batch-${b.id.slice(0, 6)}`}>
                  <td><DateCell value={b.created_at} /></td>
                  <td className="font-mono text-white/85 text-xs">{b.action}</td>
                  <td><AutoBadge value={b.status} /></td>
                  <td className="text-right text-emerald-300 tabular-nums">{b.processed_rows}</td>
                  <td className="text-right text-red-300 tabular-nums">{b.failed_rows}</td>
                  <td className="text-right text-amber-300 tabular-nums">{b.skipped_rows}</td>
                  <td className="text-white/60 text-xs truncate max-w-[220px]">{b.actor_email}</td>
                  <td className="text-right">
                    <a className="lp-btn-ghost" href={`${API_BASE}/admin/bulk/batches/${b.id}/report.csv`} target="_blank" rel="noreferrer" data-testid={`bulk-report-${b.id.slice(0, 6)}`} aria-label="Download report">
                      <Download className="w-3 h-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        )}
      </SectionCard>
    </div>
  );
}
